import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllChecklists } from '@/services/api/resources/checklist';
import { filter as getAllEntities } from '@/services/api/resources/entityItem';
import { filter as getAllGroups } from '@/services/api/resources/group';
import { filterListToOptions, mapAssigneesListToOptions, sequence, toastAndRedirect } from '@/ui/helpers';
import { ref } from 'vue';
import { SelectOptions } from '@/ui/types';
import { useEntityItemSelection } from '@/ui/pages/entityItem/useEntityItemSelection';
import { hasPermission, Permissions, user } from '@/ui/auth/auth';
import { useTask } from '@/ui/concerns';
import { useScheduleStore } from '@/stores/schedule/schedule';
import { create, get, intoEditable, update } from '@/services/api/resources/schedule';
import { Schedule } from '@/entities/schedule';
import { t } from '@/ui/i18n/i18n';
import { FormsTabNames, queryTabName } from '@/ui/components/navigation/constants';
import { versioned } from '@/services/api';

export function useSchedule(id?: number | null) {
  const canAccessPage = hasPermission(Permissions.MANAGE_CHECKLISTS);
  const store = useScheduleStore();
  const record = ref<Schedule>(store.record);
  const entityItemOptions = ref<SelectOptions>([]);
  const checklistOptions = ref<SelectOptions>([]);
  const assigneeOptions = ref<SelectOptions<string>>([]);

  const scheduleUserOptions = () => sequence(
    getAllUsers(),
    getAllGroups(),
  );

  const scheduleOptions = () => sequence(
    getAllUsers(),
    getAllGroups(),
    getAllChecklists(),
    getAllEntities(),
  );

  const { disableEntityItemSelection } = useEntityItemSelection(user, entityItemOptions);

  const setOptions = (
    users: BaseFilters,
    groups: BaseFilters,
    checklists: BaseFilters,
    entities: BaseFilters,
  ) => {
    assigneeOptions.value = mapAssigneesListToOptions(users.data, groups.data);
    checklistOptions.value = filterListToOptions(checklists, t('checklist.no-title'));
    entityItemOptions.value = filterListToOptions(entities).map(disableEntityItemSelection);
  };

  const getUserOptions = useTask(
    () => scheduleUserOptions(),
    ([users, groups]) => {
      assigneeOptions.value = mapAssigneesListToOptions(users.data, groups.data);
    },
  );

  const getOptions = useTask(
    () => scheduleOptions(),
    ([users, groups, checklists, entities]) => {
      setOptions(users, groups, checklists, entities);
    },
  );

  const getRecord = useTask(
    () => sequence(
      get(id!),
      scheduleOptions(),
    ),
    ([{ data, version }, [users, groups, checklists, entities]]) => {
      store.setId(id!);
      setOptions(users, groups, checklists, entities);
      store.setRecord(intoEditable(data));
      store.setVersion(version);
      record.value = intoEditable(data);
    },
  );

  const createRecord = useTask(
    () => create(store.record),
    () => toastAndRedirect(t('schedules.created'), 'forms', { [queryTabName]: FormsTabNames.SCHEDULES }),
  );

  const updateRecord = useTask(
    () => update(store.id!, versioned(store.record, store.version)),
    () => toastAndRedirect(t('schedules.updated'), 'forms', { [queryTabName]: FormsTabNames.SCHEDULES }),
  );

  const updateUserOptions = () => {
    getUserOptions.run();
  };

  return {
    record,
    store,
    canAccessPage,
    entityItemOptions,
    checklistOptions,
    assigneeOptions,
    getOptions,
    getRecord,
    createRecord,
    updateRecord,
    getUserOptions,
    updateUserOptions,
  };
}
